import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { AppBar, Autocomplete, Box, Button, Divider, FilterOptionsState, FormControlLabel, FormGroup, IconButton, ListItem, ListItemText, MenuItem, Switch, TextField, Toolbar, Typography, useMediaQuery, type Theme } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { type Station, type User } from '../../../../shared/types';
import { drawerStateAtom } from '../../../atoms';
import { useAdminStations } from '../../../hooks/useAdminStations';
import { useCreateAdminUser, useDeleteAdminUser, useUpdateAdminUser } from '../../../hooks/useAdminUsers';
import { useDeleteDialog } from '../../../hooks/useDeleteDialog';
import { useSimpleAlert } from '../../../hooks/useSimpleAlert';
import { closedMixinToolbar, openedMixinToolbar } from '../../helpers';
import { UserApiKey } from './UserApiKey';
import { useNavigate } from 'react-router';

type Props = {
    user?: User | null;
};

const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    role: 'user',
    permittedStations: [] as string[],
    allStations: false,
} as User;

const validationSchema = yup.object().shape({
    firstName: yup.string().required('Required'),
    lastName: yup.string().required('Required'),
    email: yup.string().email().required('Required'),
    role: yup.string().oneOf(['user', 'admin', 'vendor']).required('Required'),
    permittedStations: yup.array().of(yup.string().required()).required(),
    allStations: yup.boolean().required(),
});

export const UserForm = ({ user = null }: Props) => {
    const [drawerState] = useAtom(drawerStateAtom);
    const [apiKeyUserEmail, setApiKeyUserEmail] = useState<null | string>(null);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { data: stations, isLoading } = useAdminStations();
    const { mutate: updateAdminUser } = useUpdateAdminUser();
    const { mutate: createAdminUser } = useCreateAdminUser();
    const { mutate: deleteAdminUser } = useDeleteAdminUser();
    const { DeleteDialog, showDialog: showDeleteDialog } = useDeleteDialog();
    const { showSimpleAlert } = useSimpleAlert();
    const navigate = useNavigate();

    const onBack = () => {
        navigate('/admin/users');
    };

    const onSubmit = async (values: User, { setErrors }: FormikHelpers<User>) => {
        const { _id, allStations, permittedStations } = values;
        const strippedValues = validationSchema.cast(values, { stripUnknown: true });

        if (!allStations && !(permittedStations?.length > 0)) {
            setErrors({ permittedStations: 'At least one station must be permitted' });

            return;
        }

        if (_id) {
            updateAdminUser(
                { id: _id.toString(), data: strippedValues },
                {
                    onSuccess: (data) => {
                        showSimpleAlert({ title: 'User successfully updated' });
                        formik.resetForm({ values: data });
                    },
                },
            );
        } else {
            createAdminUser(strippedValues, {
                onSuccess: () => {
                    showSimpleAlert({ title: 'User successfully created' });
                    formik.resetForm({ values: initialValues });
                },
            });
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleStationFilterOptions: ((options: Station[], state: FilterOptionsState<Station>) => Station[]) | undefined = (options, { inputValue }) => {
        const filterRegex = new RegExp(inputValue, 'gi');

        return options.filter(({ name, stationId }) => `${name} ${stationId}`.match(filterRegex));
    };

    const handleAllStationsChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (checked) formik.setFieldValue('permittedStations', []);

        formik.setFieldValue('allStations', checked);
        formik.validateField('permittedStations');
    };

    const handlePermittedStationsChange = (_: React.SyntheticEvent<Element, Event>, value: Station[]) => {
        const stations = value.map(({ _id }) => _id.toString());

        formik.setFieldValue('permittedStations', stations);
        formik.setFieldValue('allStations', false);
        formik.validateField('permittedStations');
    };

    const handleShowDeleteDialog = () => {
        showDeleteDialog({ title: `Confirm DELETE of user ${user?.firstName} ${user?.lastName}`, onDelete: handleDeleteUser });
    };

    const handleDeleteUser = () => {
        if (user) {
            deleteAdminUser(user._id.toString(), {
                onSuccess: () => {
                    showSimpleAlert({ title: 'User successfully deleted', severity: 'info' });
                    onBack();
                },
                onError: () => {
                    showSimpleAlert({ title: 'Unable to delete the user', severity: 'error' });
                },
            });
        }
    };

    const handleOpenCreateApiKey = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>, email: string) => {
        setApiKeyUserEmail(email);
    };

    useEffect(() => {
        const values = user ? { ...initialValues, ...user } : initialValues;

        formik.resetForm({ values });
    }, [user]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.sm, p: 3, pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2 }}>
                        <Button variant="outlined" size="large" sx={{ minWidth: 0, pl: 1, pr: 1 }} onClick={onBack} startIcon={<NavigateBeforeIcon />}>
                            Users
                        </Button>
                        {user && (
                            <Typography variant="h6" gutterBottom={false}>
                                {`${user.firstName} ${user.lastName}`}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        User Details
                    </Typography>
                    <Divider />
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'scroll',
                    }}>
                    <form noValidate autoComplete="off">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                maxWidth: (theme) => theme.breakpoints.values.sm,
                                p: 3,
                                mb: 8,
                            }}>
                            <TextField
                                aria-describedby="firstName-text"
                                label="First Name"
                                placeholder="First Name"
                                type="text"
                                name="firstName"
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.firstName)}
                                helperText={formik.touched.firstName && formik.errors.firstName}
                            />
                            <TextField
                                aria-describedby="lastName-text"
                                label="Last Name"
                                placeholder="Last Name"
                                type="text"
                                name="lastName"
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.lastName)}
                                helperText={formik.touched.lastName && formik.errors.lastName}
                            />
                            <TextField
                                aria-describedby="email-text"
                                label="Email"
                                placeholder="Email"
                                type="text"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                            />
                            {formik.values.role !== 'admin' && (
                                <TextField
                                    select
                                    label="Role"
                                    name="role"
                                    value={formik.values.role || 'user'}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.role)}
                                    helperText={formik.touched.role && formik.errors.role}>
                                    <MenuItem value="user">User</MenuItem>
                                    <MenuItem value="vendor">Vendor</MenuItem>
                                </TextField>
                            )}
                            {formik.values.role === 'admin' && <TextField aria-describedby="role-text" label="Role" placeholder="Role" type="text" value={'Admin'} disabled={true} />}
                            {formik.values.role === 'vendor' && formik.values.email && (
                                <Button onClick={(event) => handleOpenCreateApiKey(event, formik.values.email)} variant="outlined" size="large">
                                    Generate ApiKey
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
                                <Typography variant="subtitle1">Station Access</Typography>
                                <Divider />
                                <FormGroup aria-label="position" row>
                                    <FormControlLabel
                                        control={<Switch color="primary" name="allStations" onChange={handleAllStationsChange} checked={Boolean(formik.values.allStations)} value={formik.values.allStations} />}
                                        label="All Stations"
                                        labelPlacement="end"
                                    />
                                </FormGroup>
                                <Autocomplete
                                    multiple
                                    options={stations ?? []}
                                    filterSelectedOptions={true}
                                    getOptionLabel={(option) => option.stationId}
                                    getOptionKey={({ _id }: Station) => _id.toString()}
                                    filterOptions={handleStationFilterOptions}
                                    renderOption={(props: object, { stationId, description }: Station) => {
                                        return (
                                            <ListItem {...props}>
                                                <ListItemText primary={stationId} secondary={description} />
                                            </ListItem>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Permitted Stations"
                                            error={Boolean(!formik.values.allStations && formik.errors.permittedStations)}
                                            helperText={!formik.values.allStations ? formik.errors.permittedStations : ''}
                                        />
                                    )}
                                    disabled={Boolean(formik.values.allStations)}
                                    loading={isLoading}
                                    onChange={handlePermittedStationsChange}
                                    value={(stations || []).filter(({ _id }) => formik.values.permittedStations?.includes(_id.toString()))}
                                />
                            </Box>
                        </Box>
                    </form>
                </Box>
            </Box>
            <UserApiKey email={apiKeyUserEmail} onClose={() => setApiKeyUserEmail(null)} />
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Toolbar
                    sx={(theme) => ({
                        ...(isNotSm &&
                            drawerState && {
                                ...openedMixinToolbar(theme),
                            }),
                        ...(isNotSm &&
                            !drawerState && {
                                ...closedMixinToolbar(theme),
                            }),
                        maxWidth: (theme) => theme.breakpoints.values.sm,
                    })}>
                    {user && (
                        <IconButton aria-label="delete" size="large" onClick={handleShowDeleteDialog}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <DeleteDialog />
        </Box>
    );
};
